import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building, Building2, Search, Settings, Sparkles, User } from 'lucide-react';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/new-ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/new-ui/dialog';
import { env } from '@/constants/env';
import { getSearchAllQueryOptions, type SearchAllResult } from '@/features/search/api/search';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

import { useTenantSwitcher } from './tenant-switcher-dialog';
import { type ShellNavItem, useShellNav } from './use-shell-nav';

const RECENT_IN_SEARCH = 5;
const SEARCH_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type NavTarget =
  | { to: ShellNavItem['url'] | '/profile' }
  | { to: '/tenant/$tenantId/users'; params: { tenantId: string } };

type SearchResult = {
  id: string;
  title: string;
  subtitle?: string | undefined;
  type: 'estate' | 'project' | 'tool' | 'nav';
  icon: React.ElementType;
  thumbnailId?: string | undefined;
  target?: NavTarget;
  estateId?: string;
  projectId?: string;
};

type EstateRow = SearchAllResult['estates'][number];
type ProjectRow = SearchAllResult['projects'][number];

const isMac = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

const getThumbnailUrl = (mediaId: string) => `${env.API_URL}/images/${mediaId}?size=thumbnail`;

const estateToResult = (estate: EstateRow): SearchResult => ({
  id: estate.id,
  title: estate.name ?? '',
  subtitle: estate.address?.address ?? undefined,
  type: 'estate',
  icon: Building,
  thumbnailId: estate.thumbnail?.mediaId ?? undefined,
  estateId: estate.id,
});

const projectToResult = (project: ProjectRow): SearchResult => ({
  id: project.id,
  title: project.name ?? '',
  subtitle: project.address?.address ?? undefined,
  type: 'project',
  icon: Building2,
  thumbnailId: project.thumbnail?.mediaId ?? undefined,
  projectId: project.id,
});

/** Icon tile of a result. Shows the thumbnail once it has loaded, the icon until then. */
const ResultThumbnail = ({
  icon: Icon,
  thumbnailId,
}: {
  icon: React.ElementType;
  thumbnailId?: string | undefined;
}) => {
  const [loadedId, setLoadedId] = useState<string>();
  const loaded = thumbnailId !== undefined && loadedId === thumbnailId;

  return (
    <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
      <Icon className="h-4 w-4 text-muted-foreground" />
      {thumbnailId ? (
        <img
          src={getThumbnailUrl(thumbnailId)}
          alt=""
          className={loaded ? 'absolute inset-0 h-full w-full object-cover' : 'hidden'}
          onLoad={() => setLoadedId(thumbnailId)}
        />
      ) : null}
    </div>
  );
};

export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Same validated tenant as the user menu, never a stale id from storage
  const { selectedTenant, me } = useTenantSwitcher();
  const { mainItems, toolItems: toolNavItems, showAiRendering } = useShellNav();
  // Same scope as the estates page: one tenant when selected, everything in "All tenants" mode
  const tenantFilter = selectedTenant ? { tenantId: selectedTenant.id } : {};

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchString, setSearchString] = useState('');

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery !== '';

  const debouncedSetSearchString = useDebouncedCallback(setSearchString, SEARCH_DEBOUNCE_MS);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    const trimmed = value.trim();
    if (trimmed === '') {
      // Back to the empty state right away, no waiting for the debounce
      debouncedSetSearchString.cancel();
      setSearchString('');
      return;
    }
    debouncedSetSearchString(trimmed);
  };

  // Reset the query whenever the dialog closes, however it was closed
  useEffect(() => {
    if (!open) {
      debouncedSetSearchString.cancel();
      setQuery('');
      setSearchString('');
    }
  }, [open, debouncedSetSearchString]);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Server data is fetched only while the dialog is open
  const recentQuery = useQuery({
    ...getSearchAllQueryOptions({
      query: {
        searchType: 'estate',
        pageSize: RECENT_IN_SEARCH,
        filters: { searchString: '', ...tenantFilter },
        sort: { by: 'updatedAt', order: 'desc' },
      },
    }),
    enabled: open,
  });

  const searchQuery = useQuery({
    ...getSearchAllQueryOptions({
      query: {
        searchType: 'all',
        pageSize: SEARCH_PAGE_SIZE,
        filters: { searchString, ...tenantFilter },
      },
    }),
    // Never show results of an earlier query while a new one loads
    placeholderData: undefined,
    enabled: open && searchString !== '',
  });

  const recent = useMemo<SearchResult[]>(() => {
    const data = recentQuery.data;
    if (!Array.isArray(data)) return [];
    return (data as EstateRow[]).slice(0, RECENT_IN_SEARCH).map(estateToResult);
  }, [recentQuery.data]);

  const serverResults = useMemo(() => {
    const data = searchQuery.data as SearchAllResult | undefined;
    if (!isSearching || searchString !== trimmedQuery || !data || Array.isArray(data)) {
      return { estates: [] as SearchResult[], projects: [] as SearchResult[] };
    }
    return {
      estates: (data.estates ?? []).map(estateToResult),
      projects: (data.projects ?? []).map(projectToResult),
    };
  }, [searchQuery.data, isSearching, searchString, trimmedQuery]);

  const toResult = (item: ShellNavItem, type: 'nav' | 'tool'): SearchResult => ({
    id: item.id,
    title: item.title,
    type,
    icon: item.icon,
    target: { to: item.url },
  });

  const navItems: SearchResult[] = [
    ...mainItems.map((item) => toResult(item, 'nav')),
    { id: 'profile', title: t('profile'), type: 'nav', icon: User, target: { to: '/profile' } },
    ...(me.data.canAccessSetup && selectedTenant
      ? [
          {
            id: 'setup',
            title: t('setup'),
            type: 'nav',
            icon: Settings,
            target: { to: '/tenant/$tenantId/users', params: { tenantId: selectedTenant.id } },
          } satisfies SearchResult,
        ]
      : []),
  ];
  const toolItems = toolNavItems.map((item) => toResult(item, 'tool'));

  // Client-side filtering for navigation and tools, the server filters its own results
  const q = trimmedQuery.toLowerCase();
  const matchesQuery = (item: SearchResult) => item.title.toLowerCase().includes(q);
  const filteredNav = isSearching ? navItems.filter(matchesQuery) : [];
  const filteredTools = isSearching ? toolItems.filter(matchesQuery) : [];

  const groups = [
    { heading: t('shellSearchGroupNavigation'), items: filteredNav },
    { heading: t('shellSearchGroupEstates'), items: serverResults.estates },
    { heading: t('shellSearchGroupProjects'), items: serverResults.projects },
    { heading: t('shellSearchGroupTools'), items: filteredTools },
  ];

  const hasResults = groups.some((group) => group.items.length > 0);
  const isPending = isSearching && (trimmedQuery !== searchString || searchQuery.isPending);
  const showEmpty = isSearching && !isPending && !hasResults;

  const handleSelect = (target: NavTarget) => {
    setOpen(false);
    navigate(target);
  };

  const handleSelectResult = (item: SearchResult) => {
    setOpen(false);
    if (item.estateId) {
      navigate({ to: '/estates/$estateId', params: { estateId: item.estateId } });
    } else if (item.projectId) {
      navigate({ to: '/projects/$projectId', params: { projectId: item.projectId } });
    } else if (item.target) {
      navigate(item.target);
    }
  };

  const mac = isMac();

  return (
    <>
      {/* Search Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative flex h-9 w-full max-w-md items-center gap-2 rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{t('shellSearchPlaceholder')}</span>
        <kbd className="hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground md:inline-flex">
          {mac ? <span className="text-xs">⌘</span> : <span className="text-xs">Ctrl</span>}K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="gap-0 overflow-hidden p-0 shadow-lg">
          <DialogTitle className="sr-only">{t('shellSearchPlaceholder')}</DialogTitle>
          <Command
            shouldFilter={false}
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
          >
            <CommandInput
              placeholder={t('shellSearchCommandPlaceholder')}
              value={query}
              onValueChange={handleQueryChange}
            />
            <CommandList>
              {isPending && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('shellSearchLoading')}
                </div>
              )}
              {showEmpty && (
                <div className="py-6 text-center text-sm">{t('shellSearchNoResults')}</div>
              )}

              {!isSearching && recent.length > 0 && (
                <CommandGroup heading={t('shellSearchRecent')}>
                  {recent.map((item) => (
                    <CommandItem
                      key={`recent-${item.id}`}
                      value={`recent-${item.id}`}
                      onSelect={() => handleSelectResult(item)}
                      className="flex items-center gap-2 py-2"
                    >
                      <ResultThumbnail icon={item.icon} thumbnailId={item.thumbnailId} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium">{item.title}</span>
                        {item.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {!isSearching && (
                <CommandGroup heading={t('shellSearchSuggestions')}>
                  <CommandItem
                    value="suggestion-estates"
                    onSelect={() => handleSelect({ to: '/estates' })}
                  >
                    <Building className="mr-2 h-4 w-4" />
                    {t('shellSearchSuggestionEstates')}
                  </CommandItem>
                  {showAiRendering && (
                    <CommandItem
                      value="suggestion-ai-rendering"
                      onSelect={() => handleSelect({ to: '/image-rendering' })}
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('shellSearchSuggestionAiRendering')}
                    </CommandItem>
                  )}
                </CommandGroup>
              )}

              {groups.map((group, index) => {
                if (group.items.length === 0) return null;

                return (
                  <div key={group.heading}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup heading={group.heading}>
                      {group.items.map((item) => (
                        <CommandItem
                          key={`${item.type}-${item.id}`}
                          value={`${item.type}-${item.id}`}
                          onSelect={() => handleSelectResult(item)}
                          className="flex items-center gap-2 py-2"
                        >
                          <ResultThumbnail icon={item.icon} thumbnailId={item.thumbnailId} />
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-sm font-medium">{item.title}</span>
                            {item.subtitle && (
                              <span className="truncate text-xs text-muted-foreground">
                                {item.subtitle}
                              </span>
                            )}
                          </div>
                          {item.type === 'project' && (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {t('shellSearchProjectLabel')}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </div>
                );
              })}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
